import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Menu } from 'lucide-react';
import Header from '../../shared/Header';
import AdminSidebar from './AdminSidebar';
import { sellerMenuItems, sellerBottomMenuItems, sellerTopNavItems, sidebarVisiblePaths, hubTopNavItems, branding } from './AdminMenuConfig.js';
import { SidebarProvider, useSidebar } from '../../../context/SidebarContext';
import { useSeller } from '../../../context/SellerContext';
import { useAuth } from '../../../context/AuthContext';
import useBranch from '../../../hooks/useBranch';

const ADMIN_ONLY_SETTING_PATHS = ['/settings/scheduler-status'];

// ─── Shared Nav Bar Renderer ────────────────────────────────────────────────

function TopNavBar({ items, prefix }) {
  const navigate = useNavigate();
  const location = useLocation();

  return items.map(item => {
    const Icon = item.icon;
    const active = item.isSidebarView
      ? sidebarVisiblePaths.includes(location.pathname)
      : location.pathname === item.path
        || (item.path === '/users' && location.pathname.startsWith('/users'));
    return (
      <button
        key={item.path}
        onClick={() => navigate(item.path)}
        className={`relative flex items-center gap-1.5 px-3.5 py-2.5 text-[13px] font-medium whitespace-nowrap transition-all ${
          active ? 'text-white' : 'text-[#bbb] hover:text-white'
        }`}
      >
        <Icon size={14} className={active ? 'text-[#f08804]' : ''} />
        {item.label}
        {active && <span className="absolute bottom-0 left-1.5 right-1.5 h-[3px] bg-[#f08804] rounded-t-full" />}
      </button>
    );
  });
}

// ─── Seller Top Nav ─────────────────────────────────────────────────────────

function SellerTopNav() {
  const navigate = useNavigate();
  const { isAdminOrManagement } = useAuth();
  const { clearSeller } = useSeller();

  const items = isAdminOrManagement()
    ? sellerTopNavItems
    : sellerTopNavItems.filter(item => !ADMIN_ONLY_SETTING_PATHS.includes(item.path));

  return (
    <div className="bg-[#232F3E] px-3 flex items-center overflow-x-auto">
      <button
        onClick={() => { clearSeller(); navigate('/sellers'); }}
        className="flex items-center gap-1.5 px-2.5 py-2.5 text-[12px] font-medium text-[#999] hover:text-white transition-colors whitespace-nowrap"
        title="Back to All Sellers"
      >
        <ArrowLeft size={14} />
        <span className="hidden sm:inline">All Sellers</span>
      </button>
      <div className="w-px h-4 bg-[#4a5568] mx-1 flex-shrink-0" />
      <TopNavBar items={items} />
    </div>
  );
}

// ─── Hub Top Nav ────────────────────────────────────────────────────────────

function HubTopNav() {
  const { isAdminOrManagement } = useAuth();

  const items = isAdminOrManagement()
    ? hubTopNavItems
    : hubTopNavItems.filter(item => !item.adminOnly);

  return (
    <div className="bg-[#232F3E] px-3 flex items-center overflow-x-auto">
      <TopNavBar items={items} />
    </div>
  );
}

// ─── Main Layout ────────────────────────────────────────────────────────────

function MobileMenuButton() {
  const { setIsCollapsed } = useSidebar();
  return (
    <button
      onClick={() => setIsCollapsed(false)}
      className="lg:hidden fixed bottom-4 right-4 z-20 w-11 h-11 bg-[#232F3E] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#37475A] transition-colors"
      title="Open menu"
    >
      <Menu size={18} />
    </button>
  );
}

export default function AdminLayout({ children }) {
  const { branchName } = useBranch();
  const { selectedSeller } = useSeller();
  const location = useLocation();

  const showSidebar = selectedSeller && sidebarVisiblePaths.includes(location.pathname);

  return (
    <SidebarProvider defaultOpen={true}>
      <div className="flex flex-col h-screen bg-[#F3F3F3] overflow-hidden">
        <Header branchName={branchName} />
        {selectedSeller ? <SellerTopNav /> : <HubTopNav />}
        <div className="flex flex-1 overflow-hidden">
          {showSidebar && (
            <AdminSidebar
              menuItems={sellerMenuItems}
              bottomMenuItems={sellerBottomMenuItems}
              branding={branding}
              defaultOpen={true}
            />
          )}
          <main className="flex-1 overflow-auto bg-[#F3F3F3]">
            {children}
          </main>
        </div>
        {showSidebar && <MobileMenuButton />}
      </div>
    </SidebarProvider>
  );
}
